/**
 * EL-003 — o seletor de cenários, de volta, NO TÍTULO da tela do cenário (09/10/2026).
 *
 * Pedido literal (lote de 09/10/2026, item 4): "Elimine a tela intermediária que existe com
 * os cenários listados. Leve isso para dentro da tela que já mostra os dados do cenário. No
 * campo superior esquerdo leve as funcionalidades que estão nessa tela (criação de novo
 * cenário, renomear cenário, copiar cenário, excluir cenário)."
 * Decisão do usuário: o nome do cenário no alto do conteúdo vira o botão — não ocupa altura
 * nova (pedido de 05/07/2026: "Está começando um espaço de tela de que não disponho").
 *
 * O QUE ELE MOSTRA: os cenários ATIVOS do empreendimento (nome + selo, ✓ no aberto, SEM data
 * nem "Com fluxo mensal" — pedido de 27/07 sobre o EL-176), um ⋮ por linha (EL-193) com
 * Renomear · Copiar · Arquivar, o "+ Novo cenário n/5" (EL-177) no rodapé e a seção
 * "Arquivados (n)" recolhida (EL-194) com Restaurar e — só o Admin Hype — Excluir de vez.
 * Quem pode o quê vem de fora (`poderes`); a regra de verdade é do banco (0028).
 *
 * ⚠ IDs DOM NOVOS. `#emp-btn`, `#emp-tree`, `#emp-tree-list`, `#emp-btn-lbl` e `#tree-menu`
 * são da v67 e o `simulador.js` ESCREVE neles (`refreshEmpSelect`/`renderEmpTree`): reusá-los
 * faria a árvore falsa da v67 — com criar/copiar/excluir que só mexem na memória — aparecer
 * dentro deste painel. As classes também são próprias (`.selcen*`): as `.emptree*` têm
 * regras sem escopo no estilo.css, e regra sem escopo vaza.
 *
 * ⚠ NASCE DESLIGADO. A montagem do simulador varre `body > .ov` (`desmontar` com
 * `apenasSobreposicoes`) — um diálogo de Novo/Renomear/Copiar aberto nos primeiros segundos
 * sumiria no meio, e a promessa dele nunca se resolveria. Quem cria o seletor chama
 * `liberar()` quando a montagem termina (com sucesso ou não).
 */
import { h, limpar } from '../dom';
import { icone } from '../icones';
import { marcarIds } from './ids';
import { botaoMenu, fecharMenu, menuAberto } from './menu';
import type { ItemMenu } from './menu';
import { LIMITE_CENARIOS, motivoSemVaga, seloCenario } from '../cenarios-regras';
import type { CenarioArquivado, CenarioResumo } from '../../../../packages/dados/src/repositorios';

export interface PoderesSeletor {
  /** Gestor ou analista: criar, renomear, copiar, arquivar, restaurar. */
  editar: boolean;
  /** Admin Hype: excluir DE VEZ (só o que está arquivado). */
  excluirDefinitivo: boolean;
  /** Admin Hype: as etiquetas EL-xxx do painel ("Mostrar IDs"). */
  marcarIds: boolean;
}

export interface AcoesSeletor {
  trocar(c: CenarioResumo): void;
  novo(): void;
  renomear(c: CenarioResumo): void;
  copiar(c: CenarioResumo): void;
  arquivar(c: CenarioResumo): void;
  restaurar(a: CenarioArquivado): void;
  excluir(a: CenarioArquivado): void;
}

export interface OpcoesSeletor {
  /** O nome do cenário aberto — é o título da página. */
  titulo: string;
  abertoId: string;
  ativos: readonly CenarioResumo[];
  /**
   * A versão publicada CORRENTE do aberto. A lista é da abertura da tela; publicar daqui (ou
   * a publicação de outra pessoa chegando pela sonda) sobe a versão, e o selo do aberto
   * diria "Nunca publicado" logo depois de publicar.
   */
  publicadaDoAberto?: () => number;
  poderes: PoderesSeletor;
  acoes: AcoesSeletor;
  /**
   * Os ativos RELIDOS do banco, a cada abertura do painel (revisão de 09/10/2026). A lista
   * `ativos` é a da abertura da tela: um cenário que um colega arquivou (ou criou) depois disso
   * aparecia como ativo — com ⋮ Renomear/Arquivar que só davam erro do banco — e, arquivado, ao
   * mesmo tempo em "Arquivados". Ausente = a lista da abertura vale para sempre.
   */
  relerAtivos?(): Promise<CenarioResumo[]>;
  /** Os cenários com alteração MINHA não publicada, entre estes ativos. Perguntado a cada abertura do painel. */
  rascunhos(ativos: readonly CenarioResumo[]): Promise<Set<string>>;
  /** Os arquivados. Perguntado a cada abertura — só quando há o que fazer com eles. */
  arquivados(): Promise<CenarioArquivado[]>;
  /**
   * PONTO DE ENCAIXE do rodapé (item 7 do lote de 09/10/2026: o botão "Relatório
   * comparativo"). Chamado a cada desenho do painel — o painel é refeito a cada abertura, e
   * um nó pendurado por fora sumiria na próxima. `null` = nada (o espaço não aparece).
   */
  rodapeExtra?: () => HTMLElement | null;
}

export interface SeletorCenario {
  el: HTMLElement;
  /** Liga o botão. Ver "NASCE DESLIGADO" no cabeçalho. */
  liberar(): void;
  fechar(): void;
  /** Fecha e solta os ouvintes do documento — chamar no `aoSair` da tela. */
  destruir(): void;
}

/** Ações por arquivado — usada no painel e no estado vazio do empreendimento. */
export function linhasArquivados(
  arquivados: readonly CenarioArquivado[],
  poderes: Pick<PoderesSeletor, 'editar' | 'excluirDefinitivo'>,
  acoes: Pick<AcoesSeletor, 'restaurar' | 'excluir'>,
  antes: () => void = () => undefined,
): HTMLElement {
  return h(
    'div',
    { class: 'selcen-arq-lista' },
    ...arquivados.map((a) => {
      const selo = seloCenario(a.versaoPublicada, false);
      return h(
        'div',
        { class: 'selcen-arq-linha', 'data-cenario-id': a.id },
        h('span', { class: 'selcen-arq-ic', html: icone.arquivar(14) }),
        h('span', { class: 'selcen-txt' },
          h('span', { class: 'selcen-nm', title: a.nome }, a.nome),
          h('span', { class: `badge ${selo.tom} selcen-selo` }, selo.texto)),
        poderes.editar
          ? h('button', {
              class: 'btn out sm',
              type: 'button',
              'data-acao': 'restaurar',
              onclick: () => { antes(); acoes.restaurar(a); },
            }, h('span', { html: icone.restaurar(13) }), 'Restaurar')
          : null,
        poderes.excluirDefinitivo
          ? h('button', {
              class: 'btn out sm danger',
              type: 'button',
              'data-acao': 'excluir',
              onclick: () => { antes(); acoes.excluir(a); },
            }, h('span', { html: icone.lixeira(13) }), 'Excluir definitivamente')
          : null,
      );
    }),
  );
}

export function criarSeletorCenario(o: OpcoesSeletor): SeletorCenario {
  /* `let`: trocada pela releitura de cada abertura (`relerAtivos`). */
  let ativos = [...o.ativos];
  const temArquivados = o.poderes.editar || o.poderes.excluirDefinitivo;

  const botao = h(
    'button',
    {
      class: 'selcen-btn',
      type: 'button',
      disabled: true,
      'aria-haspopup': 'true',
      'aria-expanded': 'false',
      title: 'Trocar de cenário',
      onclick: () => (raiz.classList.contains('open') ? fechar() : abrir()),
    },
    h('span', { class: 'selcen-nome' }, o.titulo),
    h('span', { class: 'selcen-car', html: icone.chevron(18) }),
  );

  const painel = h('div', { class: 'selcen-pan', role: 'menu' });
  const raiz = h('div', { class: 'selcen', 'data-el': 'EL-003' }, h('h2', { class: 'selcen-tit' }, botao), painel);

  /* Uma resposta velha (painel fechado e aberto de novo no meio da leitura) não pode
     redesenhar por cima da mais nova. */
  let geracao = 0;

  function linhaAtiva(c: CenarioResumo, rascunhos: Set<string>): HTMLElement {
    const aberto = c.id === o.abertoId;
    const publicada = aberto && o.publicadaDoAberto !== undefined ? o.publicadaDoAberto() : c.versaoPublicada;
    const selo = seloCenario(publicada, rascunhos.has(c.id));
    const itens = (): ItemMenu[] => [
      { rotulo: 'Renomear', aoClicar: () => { fechar(); o.acoes.renomear(c); } },
      {
        rotulo: 'Copiar',
        /* curto de propósito: o menu tem 172px; a frase inteira está no rodapé do painel */
        desabilitado: motivoSemVaga(ativos.length) === null ? null : `Limite de ${LIMITE_CENARIOS} ativos`,
        aoClicar: () => { fechar(); o.acoes.copiar(c); },
      },
      { rotulo: 'Arquivar', aoClicar: () => { fechar(); o.acoes.arquivar(c); } },
    ];
    const kebab = o.poderes.editar ? botaoMenu(itens, `Ações de "${c.nome}"`) : null;
    kebab?.setAttribute('data-el', 'EL-193');
    return h(
      'div',
      { class: `selcen-linha${aberto ? ' on' : ''}`, 'data-cenario-id': c.id },
      h(
        'button',
        {
          class: 'selcen-item',
          type: 'button',
          role: 'menuitemradio',
          'aria-checked': aberto ? 'true' : 'false',
          onclick: () => {
            fechar();
            if (!aberto) o.acoes.trocar(c);
          },
        },
        h('span', { class: 'selcen-marca', html: aberto ? icone.ok(15) : '' }),
        h('span', { class: 'selcen-txt' },
          h('span', { class: 'selcen-nm', title: c.nome }, c.nome),
          h('span', { class: `badge ${selo.tom} selcen-selo`, title: selo.dica ?? null }, selo.texto)),
      ),
      kebab,
    );
  }

  function desenhar(rascunhos: Set<string>, arquivados: CenarioArquivado[] | null, falhou: boolean): void {
    limpar(painel);
    painel.appendChild(h('div', { class: 'selcen-cab' }, 'Cenários deste empreendimento'));
    painel.appendChild(h('div', { class: 'selcen-lista' }, ...ativos.map((c) => linhaAtiva(c, rascunhos))));

    if (o.poderes.editar) {
      const motivo = motivoSemVaga(ativos.length);
      painel.appendChild(
        h(
          'div',
          { class: 'selcen-rodape' },
          h(
            'button',
            {
              class: 'selcen-novo',
              type: 'button',
              'data-el': 'EL-177',
              disabled: motivo !== null,
              onclick: () => { fechar(); o.acoes.novo(); },
            },
            h('span', { html: icone.mais(15) }),
            'Novo cenário',
            h('span', { class: 'selcen-cont' }, `${ativos.length}/${LIMITE_CENARIOS}`),
          ),
          motivo === null ? null : h('div', { class: 'selcen-motivo', role: 'note' }, motivo),
        ),
      );
    }

    if (temArquivados && arquivados !== null && arquivados.length > 0) {
      const lista = linhasArquivados(arquivados, o.poderes, o.acoes, fechar);
      lista.hidden = true;
      const alternar = h(
        'button',
        {
          class: 'selcen-arq-tg',
          type: 'button',
          'aria-expanded': 'false',
          onclick: () => {
            lista.hidden = !lista.hidden;
            alternar.setAttribute('aria-expanded', lista.hidden ? 'false' : 'true');
          },
        },
        h('span', { html: icone.arquivar(15) }),
        `Arquivados (${arquivados.length})`,
        h('span', { class: 'selcen-car', html: icone.chevron(14) }),
      );
      painel.appendChild(h('div', { class: 'selcen-arq', 'data-el': 'EL-194' }, alternar, lista));
    } else if (temArquivados && falhou) {
      painel.appendChild(h('div', { class: 'selcen-motivo' }, 'Não foi possível ler os cenários arquivados agora.'));
    }

    /* PONTO DE ENCAIXE do item 7 do mesmo lote (botão "Relatório comparativo"): entra pela
       opção `rodapeExtra`. Vazio, não ocupa nada (`:empty` no estilo.css). */
    painel.appendChild(h('div', { class: 'selcen-extra', 'data-encaixe': 'relatorio-comparativo' }, o.rodapeExtra?.() ?? null));

    if (o.poderes.marcarIds) marcarIds(painel);
  }

  function aoClicarFora(e: Event): void {
    const alvo = e.target as HTMLElement | null;
    /* O ⋮ abre o menu no <body> (EL-186), fora deste painel: clicar nele não é "fora". */
    if (alvo?.closest('.selcen') === raiz || alvo?.closest('.treemenu')) return;
    fechar();
  }
  function aoTeclar(e: KeyboardEvent): void {
    /* Com o menu ⋮ aberto, o Esc é dele (ele também ouve e se fecha sozinho). Pergunta ao
       `menu.ts`, e não ao DOM: `body > .treemenu` casava com o `#tree-menu` da v67, sempre
       presente (escondido) com o simulador montado — e o Esc nunca fechava o painel. */
    if (e.key === 'Escape' && !menuAberto()) {
      fechar();
      botao.focus();
    }
  }

  function abrir(): void {
    if (botao.disabled) return;
    raiz.classList.add('open');
    botao.setAttribute('aria-expanded', 'true');
    painel.removeAttribute('data-pronto');
    document.addEventListener('mousedown', aoClicarFora, true);
    document.addEventListener('keydown', aoTeclar, true);
    /* Desenha JÁ com o que se sabe (a lista ativa), e completa com o selo de rascunho e
       os arquivados quando chegarem — o painel nunca abre vazio esperando o servidor. */
    desenhar(new Set(), null, false);
    const minha = ++geracao;
    /* Os ativos primeiro (o selo de rascunho é sobre ELES), os arquivados em paralelo. Uma
       releitura que falha não esvazia o painel: fica a lista que se tinha. */
    const ativosFrescos = o.relerAtivos !== undefined
      ? o.relerAtivos().catch(() => null)
      : Promise.resolve(null);
    void Promise.all([
      ativosFrescos.then((lista) => {
        if (lista !== null && minha === geracao) ativos = [...lista];
        return o.rascunhos(ativos).catch(() => new Set<string>());
      }),
      temArquivados ? o.arquivados().then((a) => ({ a, falhou: false }), () => ({ a: [] as CenarioArquivado[], falhou: true }))
        : Promise.resolve({ a: [] as CenarioArquivado[], falhou: false }),
    ]).then(([rasc, arq]) => {
      if (minha !== geracao || !raiz.classList.contains('open')) return;
      desenhar(rasc, arq.a, arq.falhou);
      /* As ferramentas de validação esperam por isto antes de ler o painel. */
      painel.setAttribute('data-pronto', '1');
    });
  }

  function fechar(): void {
    geracao++;
    raiz.classList.remove('open');
    botao.setAttribute('aria-expanded', 'false');
    document.removeEventListener('mousedown', aoClicarFora, true);
    document.removeEventListener('keydown', aoTeclar, true);
  }

  return {
    el: raiz,
    liberar: () => { botao.disabled = false; },
    fechar,
    destruir: () => {
      fechar();
      fecharMenu();
    },
  };
}
